import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductModal.css';
import { api } from '../../utils/apiClient';
import type { Product, ProductOptionGroup, UpsellOffer } from '../../types';

interface ProductModalProps {
  restaurantId: string;
  product: Product | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = ['main', 'side', 'drink', 'dessert', 'appetizer', 'salad', 'other'];
const PRODUCT_CLASSES = [
  { value: 'burger', label: 'Бургер / Шаурма' },
  { value: 'pizza', label: 'Пицца' },
  { value: 'sandwich', label: 'Сэндвич / Ролл' },
  { value: 'main', label: 'Основное блюдо' },
  { value: 'side', label: 'Гарнир (картофель, рис)' },
  { value: 'snack', label: 'Снек (наггетсы, стрипсы)' },
  { value: 'salad', label: 'Салат' },
  { value: 'soup', label: 'Суп' },
  { value: 'appetizer', label: 'Закуска' },
  { value: 'drink', label: 'Напиток (газировка, сок)' },
  { value: 'coffee', label: 'Кофе / Чай' },
  { value: 'dessert', label: 'Десерт / Выпечка' },
  { value: 'sauce', label: 'Соус / Добавка' },
  { value: 'combo', label: 'Комбо / Набор' },
  { value: 'other', label: 'Другое' },
];

function ProductModal({ restaurantId, product, onClose, onSuccess }: ProductModalProps) {
  const { t } = useTranslation();
  const [networkCats, setNetworkCats] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    api.get<{ categories: { id: string; name: string }[] }>('/api/menu-catalog')
      .then(({ data }) => setNetworkCats(data.categories)).catch(() => {});
  }, []);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'main',
    productClass: 'other',
    imageUrl: '',
    upsellPriority: 0,
    tags: [] as string[],
    isActive: true,
    isEditable: false,
    optionGroups: [] as ProductOptionGroup[],
    offers: [] as UpsellOffer[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description ?? '',
        price: product.price.toString(),
        category: product.category,
        productClass: (product as any).productClass ?? 'other',
        imageUrl: product.imageUrl ?? '',
        upsellPriority: product.upsellPriority ?? 0,
        tags: product.tags ?? [],
        isActive: product.isActive ?? true,
        isEditable: product.isEditable ?? false,
        optionGroups: product.optionGroups ?? [],
        offers: (product.upsellOffers ?? []).map((o) => ({
          id: o.id,
          type: o.type,
          title: o.title,
          isActive: o.isActive,
          addItems: o.addItems ?? [],
          bundlePrice: o.bundlePrice,
          compareAtPrice: o.compareAtPrice,
          sortOrder: o.sortOrder,
        })),
      });
    }
  }, [product]);

  useEffect(() => {
    api.get<{ suggested: string[]; allTags: string[] }>('/api/products/suggest-tags').then((r) => {
      setAllTags(r.data?.allTags || []);
    }).catch(() => {});
  }, []);

  const [comboItems, setComboItems] = useState<string[]>([]);
  useEffect(() => {
    if (product?.id && (product as any).productClass === 'combo') {
      api.get<{ id: string }[]>(`/api/products/${product.id}/combo-items`)
        .then(({ data }) => setComboItems(data.map(p => p.id))).catch(() => {});
    }
  }, [product?.id]);

  const [pickerProducts, setPickerProducts] = useState<Product[]>([]);
  useEffect(() => {
    api.get<Product[]>(`/api/products/restaurant/${restaurantId}`).then((r) => {
      setPickerProducts((r.data || []).filter((p) => p.id !== product?.id));
    }).catch(() => {});
  }, [restaurantId, product?.id]);

  const fetchSuggestions = useCallback(() => {
    if (!formData.name.trim() && !formData.description.trim() && !formData.category) {
      setSuggestedTags([]);
      return;
    }
    api.get<{ suggested: string[] }>('/api/products/suggest-tags', {
      params: { name: formData.name, description: formData.description, category: formData.category },
    }).then((r) => {
      setSuggestedTags(r.data?.suggested || []);
    }).catch(() => setSuggestedTags([]));
  }, [formData.name, formData.description, formData.category]);

  useEffect(() => {
    const t = setTimeout(fetchSuggestions, 400);
    return () => clearTimeout(t);
  }, [fetchSuggestions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Normalize price - handle both comma and dot as decimal separator
    const normalizedPrice = formData.price.replace(',', '.');
    const priceValue = parseFloat(normalizedPrice);

    if (isNaN(priceValue) || priceValue < 0) {
      setError('modals.validPrice');
      setLoading(false);
      return;
    }

    const hasOpts = formData.optionGroups.length > 0;
    const payload = {
      restaurantId,
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      price: priceValue,
      category: formData.category,
      productClass: formData.productClass,
      imageUrl: formData.imageUrl?.trim() || null,
      upsellPriority: parseInt(formData.upsellPriority.toString()) || 0,
      tags: formData.tags,
      isActive: formData.isActive,
      isEditable: hasOpts || formData.isEditable,
      optionGroups: hasOpts ? formData.optionGroups : null,
      offers: formData.offers,
    };

    try {
      let savedId = product?.id;
      if (product) {
        await api.patch(`/api/products/${product.id}`, payload);
      } else {
        const { data } = await api.post<{ id: string }>('/api/products', payload);
        savedId = data.id;
      }
      if (formData.productClass === 'combo' && savedId) {
        await api.put(`/api/products/${savedId}/combo-items`, { productIds: comboItems });
      }
      // Synced products resolve their customer-facing category from the network
      // catalog by externalId — keep the catalog entry in sync with the choice here.
      const extId = product?.externalId;
      if (extId) {
        const cat = networkCats.find((c) => c.name === formData.category);
        if (cat) await api.put(`/api/menu-catalog/entries/${encodeURIComponent(extId)}`, { categoryId: cat.id }).catch(() => {});
      }
      onSuccess();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      const errorMessage = msg ?? (err instanceof Error ? err.message : 'modals.failedSaveProduct');
      setError(errorMessage);
      console.error('Product save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const addCustomTag = () => {
    const tag = customTag.trim().toLowerCase().replace(/\s+/g, '-');
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setCustomTag('');
    }
  };

  const addOptionGroup = () => {
    setFormData(prev => ({
      ...prev,
      optionGroups: [...prev.optionGroups, { label: '', type: 'single', choices: [{ label: '', priceModifier: 0 }] }],
    }));
  };

  const updateOptionGroup = (idx: number, updates: Partial<ProductOptionGroup>) => {
    setFormData(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.map((g, i) => (i === idx ? { ...g, ...updates } : g)),
    }));
  };

  const removeOptionGroup = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.filter((_, i) => i !== idx),
    }));
  };

  const addChoice = (groupIdx: number) => {
    setFormData(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.map((g, i) =>
        i === groupIdx ? { ...g, choices: [...g.choices, { label: '', priceModifier: 0 }] } : g
      ),
    }));
  };

  const updateChoice = (groupIdx: number, choiceIdx: number, updates: { label?: string; priceModifier?: number }) => {
    setFormData(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.map((g, i) =>
        i === groupIdx
          ? { ...g, choices: g.choices.map((c, j) => (j === choiceIdx ? { ...c, ...updates } : c)) }
          : g
      ),
    }));
  };

  const removeChoice = (groupIdx: number, choiceIdx: number) => {
    setFormData(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.map((g, i) =>
        i === groupIdx ? { ...g, choices: g.choices.filter((_, j) => j !== choiceIdx) } : g
      ),
    }));
  };

  const addOffer = () => {
    setFormData(prev => ({
      ...prev,
      offers: [...prev.offers, {
        type: 'COMBO_UPGRADE', title: '', isActive: true,
        addItems: [], bundlePrice: 0, compareAtPrice: null, sortOrder: prev.offers.length,
      }],
    }));
  };
  const updateOffer = (idx: number, updates: Partial<UpsellOffer>) => {
    setFormData(prev => ({
      ...prev,
      offers: prev.offers.map((o, i) => (i === idx ? { ...o, ...updates } : o)),
    }));
  };
  const removeOffer = (idx: number) => {
    setFormData(prev => ({ ...prev, offers: prev.offers.filter((_, i) => i !== idx) }));
  };
  const addOfferItem = (offerIdx: number, productId: string) => {
    if (!productId) return;
    setFormData(prev => ({
      ...prev,
      offers: prev.offers.map((o, i) =>
        i === offerIdx && !o.addItems.some(a => a.productId === productId)
          ? { ...o, addItems: [...o.addItems, { productId, quantity: 1 }] }
          : o),
    }));
  };
  const removeOfferItem = (offerIdx: number, productId: string) => {
    setFormData(prev => ({
      ...prev,
      offers: prev.offers.map((o, i) =>
        i === offerIdx ? { ...o, addItems: o.addItems.filter(a => a.productId !== productId) } : o),
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? t('modals.editProduct') : t('modals.createProduct')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-box">{error.startsWith('modals.') ? t(error) : error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label>{t('modals.productName')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Big Tasty Combo"
                />
              </div>

              <div className="form-group">
                <label>{t('modals.price')}</label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => {
                    // Allow numbers, comma, and dot
                    const value = e.target.value.replace(/[^0-9,.]/g, '');
                    setFormData({ ...formData, price: value });
                  }}
                  required
                  placeholder="12.99 or 12,99"
                />
                <small>{t('modals.priceHint')}</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('modals.category')}</label>
                <select
                  value={formData.category}
                  onChange={async (e) => {
                    if (e.target.value === '__new__') {
                      const name = window.prompt('Название новой категории:')?.trim();
                      if (!name) return;
                      try {
                        const { data } = await api.post<{ id: string; name: string }>('/api/menu-catalog/categories', { name });
                        setNetworkCats(prev => [...prev, { id: data.id, name: data.name }]);
                        setFormData(prev => ({ ...prev, category: data.name }));
                      } catch { /* not a network admin — ignore */ }
                      return;
                    }
                    setFormData({ ...formData, category: e.target.value });
                  }}
                  required
                >
                  {networkCats.length > 0 ? (
                    <>
                      {!networkCats.some(c => c.name === formData.category) && formData.category && (
                        <option value={formData.category}>{formData.category}</option>
                      )}
                      {networkCats.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                      <option value="__new__">＋ Создать категорию…</option>
                    </>
                  ) : (
                    CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{t(`productCategories.${cat}`)}</option>
                    ))
                  )}
                </select>
              </div>

              <div className="form-group">
                <label>Класс (для ML)</label>
                <select
                  value={formData.productClass}
                  onChange={(e) => setFormData({ ...formData, productClass: e.target.value })}
                >
                  {PRODUCT_CLASSES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <small>Определяет поведение рекомендаций</small>
              </div>
            </div>

            {formData.productClass === 'combo' && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Что входит в комбо?</label>
                <small style={{ display: 'block', marginBottom: 8, color: '#888' }}>
                  ML не будет рекомендовать эти продукты к этому комбо
                </small>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0dcd5', borderRadius: 10, padding: 8 }}>
                  {pickerProducts.filter(p => (p as any).productClass !== 'combo').map(p => (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={comboItems.includes(p.id)}
                        onChange={(e) => setComboItems(prev =>
                          e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id)
                        )} />
                      <span>{p.name}</span>
                      <span style={{ color: '#999', fontSize: 11, marginLeft: 'auto' }}>₸{p.price}</span>
                    </label>
                  ))}
                </div>
                {comboItems.length > 0 && (
                  <small style={{ color: '#3a8f5c' }}>Выбрано: {comboItems.length} продуктов</small>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>{t('modals.upsellPriority')}</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.upsellPriority}
                  onChange={(e) => setFormData({ ...formData, upsellPriority: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <small>{t('modals.upsellPriorityHint')}</small>
              </div>
            </div>

            <div className="form-group">
              <label>{t('modals.description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('modals.descriptionPlaceholder')}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>{t('modals.imageUrl')}</label>
              <input
                type="url"
                value={formData.imageUrl}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }} />
                </div>
              )}
            </div>

            <div className="form-group tags-section">
              <label>{t('modals.tags')}</label>
              {suggestedTags.length > 0 && (
                <div className="tags-suggested">
                  <small>{t('modals.tagsSuggested')}: </small>
                  {suggestedTags.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      className={`tag-btn tag-btn-suggested ${formData.tags.includes(tag) ? 'active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      + {t(`productTags.${tag}`) || tag}
                    </button>
                  ))}
                </div>
              )}
              <div className="tags-all-label">
                <small>{t('modals.tagsAll')}</small>
              </div>
              <div className="tags-container">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    className={`tag-btn ${formData.tags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {t(`productTags.${tag}`) || tag}
                  </button>
                ))}
              </div>
              <div className="tags-custom">
                <input
                  type="text"
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
                  placeholder={t('modals.tagsCustomPlaceholder')}
                />
                <button type="button" className="tag-btn" onClick={addCustomTag}>+ {t('modals.tagsAdd')}</button>
              </div>
              <small className="tags-selected">{t('modals.selected')}: {formData.tags.join(', ') || t('productTags.none')}</small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                {t('modals.activeVisible')}
              </label>
            </div>

            <div className="form-group option-groups-section">
                <label>{t('modals.customizationOptions')}</label>
                <small className="option-groups-hint">{t('modals.optionGroupsHint')}</small>
                {formData.optionGroups.map((group, gi) => (
                  <div key={gi} className="option-group-card">
                    <div className="option-group-header">
                      <div className="option-group-label-wrap">
                        <label className="option-group-label-inner">{t('modals.questionName')}</label>
                        <input
                          type="text"
                          placeholder={t('modals.optionLabelPlaceholder')}
                          value={group.label}
                          onChange={(e) => updateOptionGroup(gi, { label: e.target.value })}
                          className="option-group-label"
                        />
                      </div>
                      <div className="option-group-type-row">
                        <select
                          value={group.type}
                          onChange={(e) => updateOptionGroup(gi, { type: e.target.value as 'single' | 'multiple' })}
                        >
                          <option value="single">{t('modals.singleChoice')}</option>
                          <option value="multiple">{t('modals.multipleChoice')}</option>
                        </select>
                        <button type="button" className="btn-remove-small" onClick={() => removeOptionGroup(gi)} title={t('common.delete')}>×</button>
                      </div>
                      <label className="option-group-required-toggle">
                        <input
                          type="checkbox"
                          checked={!!(group as any).required}
                          onChange={(e) => updateOptionGroup(gi, { required: e.target.checked } as any)}
                        />
                        <span>{t('modals.optionRequired', 'Обязательный выбор')}</span>
                      </label>
                    </div>
                    <div className="option-choices">
                      <label className="option-choices-label">{t('modals.choices')}</label>
                      {group.choices.map((choice, ci) => (
                        <div key={ci} className="option-choice-row">
                          <input
                            type="text"
                            placeholder={t('modals.choiceLabelPlaceholder')}
                            value={choice.label}
                            onChange={(e) => updateChoice(gi, ci, { label: e.target.value })}
                          />
                          <div className="option-choice-price-wrap">
                            <span className="price-prefix">₸</span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0"
                              value={choice.priceModifier !== 0 ? choice.priceModifier : ''}
                              onChange={(e) => updateChoice(gi, ci, { priceModifier: parseFloat(e.target.value) || 0 })}
                              className="price-modifier-input"
                              title={t('modals.priceModifierHint')}
                            />
                          </div>
                          <button type="button" className="btn-remove-small" onClick={() => removeChoice(gi, ci)} title={t('common.delete')}>×</button>
                        </div>
                      ))}
                      <button type="button" className="btn-add-choice" onClick={() => addChoice(gi)}>+ {t('modals.addChoice')}</button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn-add-group" onClick={addOptionGroup}>+ {t('modals.addOptionGroup')}</button>
              </div>

            <div className="form-group offers-section">
              <label>{t('modals.bundleOffers', 'Выгодные бандлы')}</label>
              <small className="option-groups-hint">
                {t('modals.bundleOffersHint', 'Когда этот товар в заказе, клиенту предложат добрать набор по спец-цене.')}
              </small>
              {formData.offers.map((offer, oi) => {
                const nameOf = (id: string) => pickerProducts.find(p => p.id === id)?.name ?? id;
                return (
                  <div key={oi} className="offer-card">
                    <div className="offer-card-top">
                      <select
                        value={offer.type}
                        onChange={(e) => updateOffer(oi, { type: e.target.value as UpsellOffer['type'] })}
                      >
                        <option value="COMBO_UPGRADE">{t('modals.offerTypeUpgrade', 'Апгрейд / комбо')}</option>
                        <option value="DISCOUNTED_ADDON">{t('modals.offerTypeAddon', 'Добор со скидкой')}</option>
                      </select>
                      <button type="button" className="btn-remove-small" onClick={() => removeOffer(oi)} title={t('common.delete')}>×</button>
                    </div>
                    <input
                      type="text"
                      placeholder={t('modals.offerTitlePlaceholder', 'Заголовок, напр. «Сделать комбо»')}
                      value={offer.title ?? ''}
                      onChange={(e) => updateOffer(oi, { title: e.target.value })}
                    />
                    <div className="offer-items">
                      {offer.addItems.map((a) => (
                        <div key={a.productId} className="offer-item-row">
                          <span className="offer-item-name">{nameOf(a.productId)}</span>
                          <input
                            type="number" min="1" className="offer-item-qty"
                            value={a.quantity}
                            onChange={(e) => updateOffer(oi, {
                              addItems: offer.addItems.map(x => x.productId === a.productId
                                ? { ...x, quantity: Math.max(1, parseInt(e.target.value) || 1) } : x),
                            })}
                          />
                          <button type="button" className="btn-remove-small" onClick={() => removeOfferItem(oi, a.productId)}>×</button>
                        </div>
                      ))}
                      <select value="" onChange={(e) => { addOfferItem(oi, e.target.value); e.target.value = ''; }}>
                        <option value="">+ {t('modals.offerAddItem', 'Добавить товар в бандл')}</option>
                        {pickerProducts
                          .filter(p => !offer.addItems.some(a => a.productId === p.id))
                          .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="offer-prices">
                      <div className="offer-price-field">
                        <label>{t('modals.offerBundlePrice', 'Цена бандла, ₸')}</label>
                        <input
                          type="number" step="1" min="0" placeholder="590"
                          value={offer.bundlePrice || ''}
                          onChange={(e) => updateOffer(oi, { bundlePrice: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="offer-price-field">
                        <label>{t('modals.offerCompareAt', 'Старая цена (зачёркнутая), ₸')}</label>
                        <input
                          type="number" step="1" min="0" placeholder="900"
                          value={offer.compareAtPrice ?? ''}
                          onChange={(e) => updateOffer(oi, { compareAtPrice: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })}
                        />
                      </div>
                    </div>
                    <label className="offer-active-toggle">
                      <input type="checkbox" checked={offer.isActive} onChange={(e) => updateOffer(oi, { isActive: e.target.checked })} />
                      <span>{t('modals.activeVisible')}</span>
                    </label>
                  </div>
                );
              })}
              <button type="button" className="btn-add-group" onClick={addOffer}>
                + {t('modals.addBundleOffer', 'Добавить выгодный бандл')}
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? t('modals.saving') : product ? t('modals.updateProduct') : t('modals.createProduct')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProductModal;

